import { useRef, useState } from "react";
import axios from "axios";
import { Send, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { waLink } from "../config/business";
import { useLanguage } from "../i18n/LanguageContext";
import { Chapter, Reveal } from "./shared";
import { ContactSideCard } from "./ContactSideCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-2xl border border-white/10 bg-[#0B1526]/80 px-5 py-4 text-base text-slate-100 placeholder:text-slate-500 outline-none shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)] transition-all duration-300 hover:border-white/20 focus:border-amber-400/60 focus:bg-[#0D1A30] focus:ring-4 focus:ring-amber-500/15";

const EMPTY = { name: "", phone: "", issue: "", location: "", message: "", website: "" };

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={`contact-${id}`} className="mb-2 block text-sm font-semibold text-slate-300">{label}</label>
    {children}
    {error && <p data-testid={`contact-error-${id}`} role="alert" className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);

const validateForm = (form, msgs) => {
  const errs = {};
  if (form.name.trim().length < 2) errs.name = msgs.name;
  if (!/^[0-9+\-() ]{6,25}$/.test(form.phone.trim())) errs.phone = msgs.phone;
  if (form.message.trim().length < 5) errs.message = msgs.message;
  return errs;
};

// Mensaje de WhatsApp con los datos del formulario (el cliente adjunta fotos en el chat)
const buildWaMessage = (form, L, issue) =>
  [
    L.intro,
    `${L.name}: ${form.name.trim()}`,
    `${L.phone}: ${form.phone.trim()}`,
    form.location.trim() ? `${L.location}: ${form.location.trim()}` : null,
    `${L.issue}: ${issue}`,
    `${L.message}: ${form.message.trim()}`,
  ].filter(Boolean).join("\n");

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const mountedAt = useRef(Date.now());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validateForm(form, t.contact.validation);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    const issue = form.issue || t.contact.issueOptions[0];
    // Abrimos WhatsApp dentro del gesto del usuario (evita bloqueadores de pop-ups)
    window.open(waLink(buildWaMessage(form, t.contact.waLabels, issue)), "_blank", "noopener,noreferrer");
    // Copia al propietario (email + registro), sin bloquear al cliente
    axios
      .post(`${API}/contact`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        issue,
        location: form.location.trim(),
        message: form.message.trim(),
        website: form.website,
        elapsed_ms: Date.now() - mountedAt.current,
      })
      .catch(() => {});
    toast.success(t.contact.successTitle, { description: t.contact.successBody });
    setForm(EMPTY);
    setSending(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-glow">
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-28">
        <Chapter title={t.contact.title} sub={t.contact.sub} />

        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          <Reveal className="lg:col-span-3">
            <form data-testid="contact-form" onSubmit={onSubmit} noValidate className="card-elevated space-y-5 rounded-3xl p-6 sm:p-8">
              <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />

              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="name" label={t.contact.name} error={errors.name}>
                  <input id="contact-name" data-testid="contact-input-name" type="text" autoComplete="name" value={form.name} onChange={set("name")} maxLength={80} className={inputCls} aria-invalid={Boolean(errors.name)} />
                </Field>
                <Field id="phone" label={t.contact.phone} error={errors.phone}>
                  <input id="contact-phone" data-testid="contact-input-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="+44 …" value={form.phone} onChange={set("phone")} maxLength={25} className={inputCls} aria-invalid={Boolean(errors.phone)} />
                </Field>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="location" label={t.contact.location}>
                  <input id="contact-location" data-testid="contact-input-location" type="text" autoComplete="postal-code" placeholder="EH1 …" value={form.location} onChange={set("location")} maxLength={120} className={inputCls} />
                </Field>
                <Field id="issue" label={t.contact.issue}>
                  <select id="contact-issue" data-testid="contact-select-issue" value={form.issue} onChange={set("issue")} className={`${inputCls} ${form.issue ? "" : "text-slate-500"}`}>
                    <option value="" disabled>—</option>
                    {t.contact.issueOptions.map((opt) => (
                      <option key={opt} value={opt} className="text-slate-100">{opt}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field id="message" label={t.contact.message} error={errors.message}>
                <textarea id="contact-message" data-testid="contact-input-message" value={form.message} onChange={set("message")} maxLength={1000} rows={4} className={`${inputCls} resize-none`} aria-invalid={Boolean(errors.message)} />
              </Field>

              <p data-testid="contact-photo-hint" className="flex items-start gap-2.5 rounded-2xl border border-sky-400/20 bg-sky-400/[0.07] px-4 py-3 text-sm leading-relaxed text-sky-100">
                <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-300" />
                {t.contact.photoHint}
              </p>

              <button data-testid="contact-form-submit-button" type="submit" disabled={sending} className="btn btn-wa w-full rounded-2xl px-8 py-4 text-lg">
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" strokeWidth={2.5} />}
                {sending ? t.contact.sending : t.contact.submit}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-2">
            <ContactSideCard />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
